import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';

export const convertHEICToJPEG = async (file: File) => {
  const blob = new Blob([file], { type: file.type });
  let jpgBlob = await heic2any({
    blob,
    toType: 'image/jpeg',
    quality: 0.5,
  });

  if (Array.isArray(jpgBlob)) {
    jpgBlob = jpgBlob[0];
  }

  const pngFile = new File([jpgBlob], file.name.split('.')[0] + '.jpeg', {
    type: 'image/jpeg',
  });
  return pngFile;
};

const sizeToMB = (size: number) => size / 1024 / 1024;

export const resizeFile = async (file: File) => {
  const MAX_SIZE_MB = 2;
  const options = {
    maxSizeMB: MAX_SIZE_MB,
  };

  if (sizeToMB(file.size) > MAX_SIZE_MB) {
    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error('Image compression error:', error);
      return file;
    }
  }

  return file;
};

/**
 * URL 에서 경로 부분만 떼어낸다.
 * presigned URL 은 `?` 뒤가 서명이라 파일명·확장자를 읽으려면 먼저 잘라내야 한다.
 * (잘라낸 값은 표시·판별용이며, 실제 요청에는 원본 URL 을 그대로 써야 한다.)
 */
export const getUrlPath = (url: string) => {
  if (!url) return '';
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
};

/** 경로에서 확장자만. 확장자가 없으면 빈 문자열. */
export const getFileExtension = (url: string) => {
  const fileName = getUrlPath(url).split('/').pop() ?? '';
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex === -1 ? '' : fileName.slice(dotIndex + 1).toLowerCase();
};

/** URL·파일명·확장자 중 무엇이 들어와도 이미지인지 판별한다. */
export const isImageFile = (value: string) => {
  if (!value) return false;
  // 확장자만 넘기는 호출부(Send.modal, Faq.modal)가 있어 추출 실패 시 값 자체를 쓴다.
  const extension = getFileExtension(value) || value.trim().toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(extension);
};

export const getExtensionFromDataURL = (dataUrl: string): string => {
  const match = dataUrl.match(/^data:(.*?);base64,/);
  if (!match) return dataUrl;

  const mimeType = match[1];
  const extensionMap: Record<string, string> = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'text/plain': 'txt',
    // 필요한 확장자 추가 가능
  };

  return extensionMap[mimeType] || dataUrl;
};

/**
 * 파일을 받아 지정한 이름으로 저장한다.
 * S3 presigned URL 은 응답을 주는 도메인이 달라 `<a download>` 의 파일명 지정이
 * 무시되고 새 탭에서 열려버린다. blob 으로 받아 저장해야 파일명이 유지된다.
 */
export const downloadFileAs = async (url: string, fileName?: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`파일 다운로드 실패 (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName || getUrlPath(url).split('/').pop() || 'download';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(objectUrl);
};
